/**
 * src/config/shieldIcons.jsx
 *
 * Viking circular shield icons for the minibar rail group buttons.
 *
 * Each design uses a completely different geometric idiom so the nine icons
 * are immediately distinguishable from one another. All fills reach the full
 * circle edge (r = 8.5) — no smaller shape sitting inside the circle.
 *
 * All SVGs use `currentColor` so they inherit the group colour from the parent
 * span in Sidebar.jsx — no extra props needed.  The parent span already applies
 * hexAlpha(color, 0.55 | 0.85 | 1.0) for inactive / hasActiveRoute / active
 * states, so fill opacity inside the SVG should be 1 (full) to avoid stacking
 * dimming that makes fills invisible at small sizes.
 *
 * ViewBox: 0 0 20 20.  Rendered at 28 × 28 px inside the 34 px circle container.
 *
 * Coordinate reference — r = 8.5, centre (10, 10):
 *   cardinal         N(10,1.5)  E(18.5,10)  S(10,18.5)  W(1.5,10)
 *   45° diagonals    NE(16.01,3.99)  SE(16.01,16.01)  SW(3.99,16.01)  NW(3.99,3.99)
 *   60° from N CW    (17.36,5.75)  (17.36,14.25)  (2.64,14.25)  (2.64,5.75)
 */

// ── 1. WORK — Gokstad 8-sector wheel ────────────────────────────────────────────
// The Gokstad burial shields were painted in 8 alternating coloured sectors.
// 4 filled sectors + 4 open sectors, separated by 8 radiating lines, boss at centre.
// Visual idiom: PIE WHEEL — strong rotational rhythm, 8-fold symmetry.
export const WorkShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* 4 alternate 45° sectors — N-facing wedges, reach r=8.5 */}
    <path d="M10,10 L10,1.5 A8.5,8.5 0 0,1 16.01,3.99 Z" fill="currentColor" />
    <path d="M10,10 L18.5,10 A8.5,8.5 0 0,1 16.01,16.01 Z" fill="currentColor" />
    <path d="M10,10 L10,18.5 A8.5,8.5 0 0,1 3.99,16.01 Z" fill="currentColor" />
    <path d="M10,10 L1.5,10 A8.5,8.5 0 0,1 3.99,3.99 Z" fill="currentColor" />
    {/* 8 dividing spokes */}
    <line x1="10" y1="1.5" x2="10" y2="18.5" stroke="currentColor" strokeWidth="0.9" />
    <line x1="1.5" y1="10" x2="18.5" y2="10" stroke="currentColor" strokeWidth="0.9" />
    <line x1="3.99" y1="3.99" x2="16.01" y2="16.01" stroke="currentColor" strokeWidth="0.9" />
    <line x1="16.01" y1="3.99" x2="3.99" y2="16.01" stroke="currentColor" strokeWidth="0.9" />
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    {/* Iron boss */}
    <circle cx="10" cy="10" r="2.3" fill="currentColor" />
  </svg>
);

// ── 2. GOVERNANCE — Nordic cross / Danebrog ─────────────────────────────────────
// Very wide + cross bands spanning the full circle diameter — structured authority.
// Visual idiom: BOLD PLUS CROSS — cardinal bands, 4 corner panels open.
export const GovernanceShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Wide horizontal band — spans full diameter */}
    <rect x="1.5" y="7.5" width="17" height="5" fill="currentColor" />
    {/* Wide vertical band — spans full diameter */}
    <rect x="7.5" y="1.5" width="5" height="17" fill="currentColor" />
    {/* Outer rim drawn last to mask rectangle bleed at corners */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    {/* Central boss */}
    <circle cx="10" cy="10" r="2.6" fill="currentColor" />
  </svg>
);

// ── 3. RISK — Saltire / danger-X ────────────────────────────────────────────────
// A bold diagonal X with 4 filled wedge sectors between the arms.
// Visual idiom: DIAGONAL X — tension and crossing paths, 4-fold diagonal symmetry.
export const RiskShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* 4 wedge sectors between the X arms */}
    <path d="M10,10 L16.01,3.99 A8.5,8.5 0 0,1 18.5,10 Z" fill="currentColor" />
    <path d="M10,10 L16.01,16.01 A8.5,8.5 0 0,1 10,18.5 Z" fill="currentColor" />
    <path d="M10,10 L3.99,16.01 A8.5,8.5 0 0,1 1.5,10 Z" fill="currentColor" />
    <path d="M10,10 L3.99,3.99 A8.5,8.5 0 0,1 10,1.5 Z" fill="currentColor" />
    {/* Bold X arms sit on top */}
    <line x1="3.99" y1="3.99" x2="16.01" y2="16.01" stroke="currentColor" strokeWidth="2.2" />
    <line x1="16.01" y1="3.99" x2="3.99" y2="16.01" stroke="currentColor" strokeWidth="2.2" />
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    {/* Boss */}
    <circle cx="10" cy="10" r="2.3" fill="currentColor" />
  </svg>
);

// ── 4. PRIVACY — Bullseye / concentric target ────────────────────────────────────
// Three alternating rings — outer filled, middle open, inner filled — like a target.
// Visual idiom: CONCENTRIC RINGS — layered data protection, radial/onion structure.
export const PrivacyShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outer filled ring: r 8.5 → 5.5 via evenodd subtraction */}
    <path
      fillRule="evenodd"
      d="M18.5,10 A8.5,8.5 0 1,0 1.5,10 A8.5,8.5 0 1,0 18.5,10 Z
         M15.5,10 A5.5,5.5 0 1,1 4.5,10 A5.5,5.5 0 1,1 15.5,10 Z"
      fill="currentColor"
    />
    {/* Inner solid disc r=3.0 — middle ring (5.5→3.0) left open */}
    <circle cx="10" cy="10" r="3.0" fill="currentColor" />
    {/* Ring boundary strokes */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <circle cx="10" cy="10" r="5.5" stroke="currentColor" strokeWidth="0.9" fill="none" />
    <circle cx="10" cy="10" r="3.0" stroke="currentColor" strokeWidth="0.8" fill="none" />
    {/* Centre point */}
    <circle cx="10" cy="10" r="1.0" fill="currentColor" />
  </svg>
);

// ── 5. ASSETS & PARTIES — Solid diamond ──────────────────────────────────────────
// A large solid diamond (square rotated 45°) with vertices at the circle's cardinal
// points — dual nature of owned assets and external relationships.
// Visual idiom: DIAMOND / RHOMBUS — angular, bold, no sectors or rings.
export const AssetsShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Solid diamond: vertices touch the circle at N/E/S/W */}
    <path d="M10,1.5 L18.5,10 L10,18.5 L1.5,10 Z" fill="currentColor" />
    {/* Inner diamond outline for depth */}
    <path
      d="M10,5.5 L14.5,10 L10,14.5 L5.5,10 Z"
      stroke="currentColor"
      strokeWidth="1.0"
      fill="none"
    />
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    {/* Centre boss */}
    <circle cx="10" cy="10" r="1.6" fill="currentColor" />
  </svg>
);

// ── 6. ASSURANCE — Hexagram / six-pointed star ───────────────────────────────────
// Two overlapping equilateral triangles form a solid Star of David — the union
// of upward and downward triangles representing complete verification.
// Visual idiom: SIX-POINTED STAR — star silhouette, 6-fold symmetry, spiky.
export const AssuranceShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Up-pointing triangle: vertices at 0°, 120°, 240° on r=8.5 */}
    <path d="M10,1.5 L17.36,14.25 L2.64,14.25 Z" fill="currentColor" />
    {/* Down-pointing triangle: vertices at 60°, 180°, 300° on r=8.5 */}
    <path d="M17.36,5.75 L10,18.5 L2.64,5.75 Z" fill="currentColor" />
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
  </svg>
);

// ── 7. RESILIENCE — Wheel with massive hub ───────────────────────────────────────
// A large solid hub disc fills the centre; 4 bold cardinal spokes bridge to the rim;
// 4 rivets anchor the spokes at the rim. Heavy, mechanical, load-bearing.
// Visual idiom: HUB-AND-SPOKE WHEEL — circular but structurally dominated by a
// huge central disc rather than sectors, very different proportion from Work.
export const ResilienceShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* 4 very thick cardinal spokes rim-to-rim */}
    <line x1="10" y1="1.5" x2="10" y2="18.5" stroke="currentColor" strokeWidth="4.0" />
    <line x1="1.5" y1="10" x2="18.5" y2="10" stroke="currentColor" strokeWidth="4.0" />
    {/* Large solid hub covers the spoke crossing and fills the centre */}
    <circle cx="10" cy="10" r="4.8" fill="currentColor" />
    {/* 4 rim rivets at cardinal points */}
    <circle cx="10" cy="2.8" r="1.5" fill="currentColor" />
    <circle cx="17.2" cy="10" r="1.5" fill="currentColor" />
    <circle cx="10" cy="17.2" r="1.5" fill="currentColor" />
    <circle cx="2.8" cy="10" r="1.5" fill="currentColor" />
    {/* Inner hub ring detail */}
    <circle cx="10" cy="10" r="2.2" stroke="currentColor" strokeWidth="0.7" fill="none" />
  </svg>
);

// ── 8. REPORTING — Bar chart ──────────────────────────────────────────────────────
// Three vertical bars of different heights rising from a baseline — data, trends,
// and executive visibility at a glance.
// Visual idiom: BAR CHART — asymmetric, data-viz, unique among all nine icons.
export const ReportingShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* 3 vertical bars (left=medium, centre=tall, right=short) */}
    <rect x="3.8" y="8.5" width="3.8" height="9" fill="currentColor" />
    <rect x="8.1" y="4.5" width="3.8" height="13" fill="currentColor" />
    <rect x="12.4" y="11" width="3.8" height="6.5" fill="currentColor" />
    {/* Baseline */}
    <line x1="2.5" y1="17.5" x2="17.5" y2="17.5" stroke="currentColor" strokeWidth="1.2" />
    {/* Outer rim drawn last to clip bar bleed at bottom */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
  </svg>
);

// ── 9. SYSTEM — Hexagonal ring frame ─────────────────────────────────────────────
// A thick hexagonal frame (outer hexagon minus inner hexagon via evenodd) with a
// solid central boss — precision geometry, interlocking structure.
// Visual idiom: HEXAGONAL FRAME — flat-top hex ring, clearly different from the
// six-pointed star (Assurance) and all sector/ring/cross/bar designs.
export const SystemShield = () => (
  <svg viewBox="0 0 20 20" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Hexagonal ring: outer r=8.5, inner r=4.5 (evenodd subtraction)
        Outer hex (pointy-top): 10,1.5  17.36,5.75  17.36,14.25  10,18.5  2.64,14.25  2.64,5.75
        Inner hex (same orientation, r=4.5):
          top (10,5.5)  (13.90,7.75)  (13.90,12.25)  (10,14.5)  (6.10,12.25)  (6.10,7.75) */}
    <path
      fillRule="evenodd"
      d="M10,1.5 L17.36,5.75 L17.36,14.25 L10,18.5 L2.64,14.25 L2.64,5.75 Z
         M10,5.5 L13.90,7.75 L13.90,12.25 L10,14.5 L6.10,12.25 L6.10,7.75 Z"
      fill="currentColor"
    />
    {/* Outer rim */}
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    {/* Solid centre boss inside the inner hex hole */}
    <circle cx="10" cy="10" r="2.2" fill="currentColor" />
  </svg>
);

// ── Export map keyed by nav group key ───────────────────────────────────────────
export const SHIELD_ICONS = {
  work: <WorkShield />,
  governance: <GovernanceShield />,
  risk: <RiskShield />,
  privacy: <PrivacyShield />,
  'assets-parties': <AssetsShield />,
  assurance: <AssuranceShield />,
  resilience: <ResilienceShield />,
  reporting: <ReportingShield />,
  system: <SystemShield />,
};
